import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Box, Stack, Typography, Button, Card, CardContent, Table } from '@mui/joy';
import { getProduct, getProducts } from '../services/api';
import { normalizeImagePath } from '../utils/imagePath';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [currentImage, setCurrentImage] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef(0);

  const priceOnRequestProductIds = new Set([5013, 5014, 5015]);

  const getProductPriceLabel = (p) => {
    if (!p) return '';
    if (priceOnRequestProductIds.has(p.id)) return 'Цена по запросу';
    return `${p.price} руб.`;
  };

  useEffect(() => {
    document.title = 'NASCAR — Каталог';
  }, []);

  useEffect(() => {
    const loadCart = () => {
      try {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          const validCart = parsedCart.filter((item) => item.quantity > 0);
          setCart(validCart);
          if (validCart.length !== parsedCart.length) {
            localStorage.setItem('cart', JSON.stringify(validCart));
          }
        }
      } catch (error) {
        console.error('Error loading cart:', error);
        setCart([]);
      }
    };
    loadCart();
    window.addEventListener('cartUpdated', loadCart);
    return () => window.removeEventListener('cartUpdated', loadCart);
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        let data = await getProduct(id);
        // Fallback for dev mode if PHP is not executed
        if (!data) {
          const all = await getProducts();
          data = all.find((p) => String(p.id) === String(id)) || null;
        }
        if (data) {
          document.title = `NASCAR — ${data.name}`;
        }
        setProduct(data);
        setCurrentImage(normalizeImagePath(data?.image) || '');
      } catch (e) {
        console.error('Failed to load product', e);
      }
      setLoading(false);
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product) {
      setCurrentImageIndex(0);
      setCurrentImage(normalizeImagePath(product.image) || '');
    }
  }, [product]);

  const addToCart = (p) => {
    const savedCart = localStorage.getItem('cart');
    let currentCart = [];
    if (savedCart) {
      try {
        currentCart = JSON.parse(savedCart);
      } catch (error) {
        console.error('Error parsing cart:', error);
      }
    }
    const existingItemIndex = currentCart.findIndex((item) => item.id === p.id);
    if (existingItemIndex !== -1) {
      currentCart[existingItemIndex].quantity += 1;
    } else {
      currentCart.push({ ...p, quantity: 1 });
    }
    localStorage.setItem('cart', JSON.stringify(currentCart));
    setCart(currentCart);
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: currentCart }));
  };

  const isInCart = (productId) => cart.some((item) => item.id === productId);

  const handleCartButtonClick = (p) => {
    if (isInCart(p.id)) {
      window.dispatchEvent(new CustomEvent('openCart'));
    } else {
      addToCart(p);
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (!touchStartX.current || !product?.images?.length) return;
    const touchEndX = e.touches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (Math.abs(diff) > 50) {
      let nextIndex;
      if (diff > 0) {
        nextIndex = currentImageIndex === product.images.length - 1 ? 0 : currentImageIndex + 1;
      } else {
        nextIndex = currentImageIndex === 0 ? product.images.length - 1 : currentImageIndex - 1;
      }
      setCurrentImageIndex(nextIndex);
      setCurrentImage(normalizeImagePath(product.images[nextIndex]));
      touchStartX.current = 0;
    }
  };

  const handleTouchEnd = () => {
    touchStartX.current = 0;
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', px: 2.5, py: 2, minHeight: '600px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography level="h2">Загрузка товара...</Typography>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ width: '100%', px: 2.5, py: 2, minHeight: '600px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
        <Typography level="h2">Товар не найден</Typography>
        <Button component={Link} to="/catalog" startDecorator={<ArrowBackIcon />}>Вернуться в каталог</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: { xs: 2, md: 4 }, py: 3, minHeight: '100vh', bgcolor: '#ffffff' }}>
      <Button
        variant="plain"
        color="neutral"
        startDecorator={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Назад
      </Button>

      <Box sx={{
        display: 'flex',
        gap: { xs: 2, md: 4 },
        flexDirection: { xs: 'column', md: 'row' },
      }}>
        {/* Media */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{ width: '100%', borderRadius: '20px', overflow: 'hidden' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {currentImage && currentImage.endsWith('.mp4') ? (
              <video
                src={currentImage}
                muted
                autoPlay
                loop
                style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'contain' }}
              />
            ) : (
              <img
                src={currentImage || normalizeImagePath(product.image) || '/placeholder.svg'}
                alt={product.name}
                style={{ width: '100%', height: 'auto', maxHeight: '500px', objectFit: 'contain' }}
              />
            )}
          </Box>

          {product.images && product.images.length > 0 && (
            <Box sx={{ width: '100%', maxWidth: '600px', margin: '0 auto' }}>
              <Box sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                width: '100%',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                pb: 1,
              }}>
                {product.images.map((media, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: '120px',
                      height: '105px',
                      minWidth: '120px',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      border: currentImageIndex === index ? '2px solid rgb(255, 0, 0)' : 'none',
                      position: 'relative',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                    onClick={() => {
                      setCurrentImage(normalizeImagePath(media));
                      setCurrentImageIndex(index);
                    }}
                  >
                    {media.endsWith('.mp4') ? (
                      <>
                        <video
                          src={normalizeImagePath(media)}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Box sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '40px',
                          height: '40px',
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <span style={{ color: 'white', fontSize: '20px' }}>▶</span>
                        </Box>
                      </>
                    ) : (
                      <img
                        src={normalizeImagePath(media)}
                        alt={`${product.name} - фото ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Info */}
        <Box sx={{ flex: 1 }}>
          <Typography level="h2" sx={{ mb: 1 }}>
            {product.name}
          </Typography>
          <Typography level="body-lg" sx={{ mb: 2 }}>
            {product.description}
          </Typography>

          <Typography level="title-md" color={product.quantity > 0 ? 'success' : 'danger'}>
            {product.quantity > 0 ? 'В наличии' : 'Нет в наличии'}
          </Typography>

          <Typography level="h3" color="danger" sx={{ mb: 2 }}>
            {getProductPriceLabel(product)}
          </Typography>

          <Button
            variant={isInCart(product.id) ? 'soft' : 'solid'}
            disabled={product.quantity <= 0}
            color="danger"
            fullWidth
            size="lg"
            sx={{ borderRadius: 250, py: 1.5, fontSize: '1.1rem' }}
            onClick={(e) => {
              e.stopPropagation();
              handleCartButtonClick(product);
            }}
          >
            {product.quantity > 0 ? (isInCart(product.id) ? 'В корзине' : 'Добавить в корзину') : 'Нет в наличии'}
          </Button>

          {product.characteristics && product.characteristics.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography level="h4" sx={{ mb: 2 }}>Характеристики:</Typography>
              <Table
                aria-label="product characteristics"
                sx={{
                  '& tr > *:first-child': { width: '40%', fontWeight: 'bold' },
                  '& tr > *:last-child': { width: '60%' },
                  '& tr:last-child > *': { borderBottom: 'none' },
                }}
              >
                <tbody>
                  {product.characteristics.map((char, index) => {
                    const [key, value] = char.split(':').map(item => item.trim());
                    return (
                      <tr key={index}>
                        <td>{key}</td>
                        <td>{value}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Box>

      {product.reviews && product.reviews.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography level="h2" sx={{ mb: 2 }}>Отзывы</Typography>
          <Stack spacing={2}>
            {product.reviews.map((review) => (
              <Card key={review.id} variant="outlined" sx={{ background: 'none' }}>
                <CardContent>
                  <Typography variant="h6">{review.name}</Typography>
                  <Stack direction="row" spacing={0.5}>
                    {Array.from({ length: review.stars }).map((_, index) => (
                      <span key={index} role="img" aria-label="star">⭐</span>
                    ))}
                  </Stack>
                  <Typography variant="body2">{review.comment}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export default ProductPage;
